import React from 'react'
import { NAV_DEFS, type MenuKey } from '@/modules/navigation/menus/defs'
import { ICONS, type IconId } from '@/modules/navigation/menus/icons'
import { DraggableList } from '@/shared/ui/DraggableList'
import { Switch } from '@/shared/ui'
import { Input, Button, Modal, Label, Card, CardContent } from '@/shared/ui'
import { getNavConfig, setNavConfig, type NavConfigItem } from '../services/navSettingsService'
import SaveFeedbackModal from '@/shared/ui/SaveFeedbackModal'
import { emitNavChanged } from '@/modules/navigation/events'

type Role = 'master'|'member'|'afiliado'

export default function ManageMenus() {
  return (
    <div className="grid gap-8 grid-cols-1">
      <Card><CardContent className="pt-4"><RoleSection role="master" title="Menus do Master" /></CardContent></Card>
      <Card><CardContent className="pt-4"><RoleSection role="member" title="Menus do Membro" /></CardContent></Card>
      <Card><CardContent className="pt-4"><RoleSection role="afiliado" title="Menus do Afiliado" /></CardContent></Card>
    </div>
  )
}

function RoleSection({ role, title }: { role: Role; title: string }) {
  const [items, setItems] = React.useState<Array<{ key: MenuKey; label: string; visible: boolean; icon?: IconId }>>([])
  const [saving, setSaving] = React.useState(false)
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const [editing, setEditing] = React.useState<{ key: MenuKey; label: string; icon?: IconId } | null>(null)
  const [iconQuery, setIconQuery] = React.useState('')
  const [saved, setSaved] = React.useState(false)

  const defs = React.useMemo(() => NAV_DEFS.filter(d => d.roles.includes(role)), [role])

  const extrasRef = React.useRef<NavConfigItem[]>([])
  const load = React.useCallback(async () => {
    setLoading(true); setError(null)
    try {
      const cfg = await getNavConfig(role)
      const byKey = new Map(defs.map(d => [d.key, d]))
      // Preserve unknown items (extras) to avoid dropping metadata stored alongside nav items
      extrasRef.current = (cfg || []).filter(c => !byKey.has(c.key as any))
      const ordered = (cfg && cfg.length ? cfg : defs.map(d => ({ key: d.key, label: d.label, visible: true, icon: d.icon } as NavConfigItem)))
        .map(c => ({ key: c.key as MenuKey, label: (c.label || byKey.get(c.key as MenuKey)?.label || ''), visible: c.visible !== false, icon: (c.icon as IconId) || (byKey.get(c.key as MenuKey)?.icon as IconId) }))
        .filter(c => byKey.has(c.key))
      const rest = defs.filter(d => !ordered.find(o => o.key === d.key)).map(d => ({ key: d.key, label: d.label, visible: true, icon: d.icon as IconId }))
      setItems([...ordered, ...rest])
    } catch (e: any) { setError(e?.message ?? 'Falha ao carregar') }
    finally { setLoading(false) }
  }, [role, defs])

  React.useEffect(() => { load() }, [load])

  function onReorder(ids: string[]) {
    const map = new Map(items.map(i => [i.key, i]))
    setItems(ids.map(id => map.get(id as MenuKey)!).filter(Boolean))
  }

  function updateItem(key: MenuKey, patch: Partial<{ label: string; visible: boolean; icon?: IconId }>) {
    setItems(s => s.map(i => (i.key === key ? { ...i, ...patch } : i)))
  }

  async function onSave() {
    setSaving(true); setError(null)
    try {
      const payload: NavConfigItem[] = items.map(i => ({ key: i.key, label: i.label, visible: i.visible, icon: i.icon }))
      // Re-attach preserved extras so we don't lose out-of-band settings (e.g., layout)
      const merged = [...extrasRef.current, ...payload]
      await setNavConfig(role, merged)
      setSaved(true)
    } catch (e: any) { setError(e?.message ?? 'Falha ao salvar') }
    finally { setSaving(false) }
  }

  return (
    <div>
      <div className="mb-3 text-sm font-medium">{title}</div>
      {error && <div className="mb-2 rounded-md border border-destructive/30 bg-destructive/10 p-2 text-sm text-destructive-foreground">{error}</div>}
      {loading ? (
        <div className="text-sm text-foreground/70">Carregando...</div>
      ) : (
        <DraggableList
          items={items.map(i => ({ id: i.key, label: i.label, right: (
            <div className="flex items-center gap-3">
              {!(role==='master' && String(i.key)==='settings') && (
                <label className="flex items-center gap-1 text-xs text-foreground/70">
                  <span>Visível</span>
                  <Switch checked={i.visible} onChange={e => updateItem(i.key, { visible: (e.target as HTMLInputElement).checked })} />
                </label>
              )}
              <Button variant="outline" className="h-8 px-2 text-xs" onClick={()=> setEditing({ key: i.key, label: i.label, icon: i.icon })}>Editar</Button>
            </div>
          ) }))}
          onReorder={onReorder}
        />
      )}
      <div className="mt-3">
        <Button onClick={onSave} disabled={saving || loading}>{saving ? 'Salvando...' : 'Salvar alterações'}</Button>
      </div>

      <Modal open={!!editing} onOpenChange={(v)=> !v && setEditing(null)} className="p-6">
        {editing && (
          <div className="space-y-4">
            <div className="text-lg font-semibold">Editar menu</div>
            <div className="grid gap-2">
              <Label>Nome</Label>
              <Input value={editing.label} onChange={(e)=> setEditing({ ...editing!, label: e.target.value })} />
            </div>
            <div className="grid gap-2">
              <Label>Ícone (Lucide)</Label>
              <Input placeholder="Pesquisar ícone" value={iconQuery} onChange={(e)=> setIconQuery(e.target.value)} />
              <IconGrid query={iconQuery} value={editing.icon} onChange={(id)=> setEditing({ ...editing!, icon: id })} />
              <div className="text-xs text-foreground/70">Biblioteca: lucide-react. Use nomes como "settings", "users", "credit-card".</div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={()=> setEditing(null)}>Cancelar</Button>
              <Button onClick={()=> { if (editing) updateItem(editing.key, { label: editing.label.trim() || editing.label, icon: editing.icon }); setEditing(null) }}>Salvar</Button>
            </div>
          </div>
        )}
      </Modal>

      <SaveFeedbackModal open={saved} onClose={()=> { setSaved(false); emitNavChanged() }} title="Alterações salvas" description="A configuração dos menus foi atualizada com sucesso." />
    </div>
  )
}

function IconGrid({ query, value, onChange }: { query: string; value?: IconId; onChange: (id: IconId)=>void }) {
  const idsAll = React.useMemo(()=> Object.keys(ICONS).filter(id => id.toLowerCase().includes(query.toLowerCase())), [query])
  const [limit, setLimit] = React.useState(40)
  const onScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 24) {
      setLimit((l) => Math.min(idsAll.length, l + 40))
    }
  }
  React.useEffect(()=> setLimit(40), [idsAll.join('|')])
  const ids = idsAll.slice(0, limit)
  return (
    <div className="grid grid-cols-4 gap-2 max-h-72 overflow-auto" onScroll={onScroll}>
      {ids.map((id) => {
        const Cmp = (ICONS as any)[id]
        const active = value === id
        return (
          <button key={id} type="button" onClick={()=> onChange(id as IconId)}
            className={["flex flex-col items-center gap-1 rounded-md border px-2 py-2 text-xs",
              active ? 'border-primary ring-2 ring-primary/60' : 'border-border hover:bg-muted/30'].join(' ')}>
            <Cmp className="h-5 w-5" />
            <span className="truncate w-full">{id}</span>
          </button>
        )
      })}
    </div>
  )
}

